import React from "react";
import chroma from "chroma-js";

import { DonutChart } from "../charts/DonutChart";
import { ChartLayout, layoutHeight, layoutWidth } from "../charts/chartTheme";
import { Language } from "../Language";

/** Standard & Poor's long-term issuer scale, highest credit quality first (AAA → D). */
const SP_RATING_ORDER: string[] = [
    "AAA",
    "AA+",
    "AA",
    "AA-",
    "A+",
    "A",
    "A-",
    "BBB+",
    "BBB",
    "BBB-",
    "BB+",
    "BB",
    "BB-",
    "B+",
    "B",
    "B-",
    "CCC+",
    "CCC",
    "CCC-",
    "CC",
    "C",
    "RD",
    "SD",
    "D",
    "NR",
    "NOTRATED",
    "UNRATED"
];

function normalizeRatingKey(rating: string): string {
    return String(rating ?? "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "")
        .replace(/_/g, "");
}

const SP_RATING_SORT_INDEX: Record<string, number> = {};
SP_RATING_ORDER.forEach((label, index) => {
    SP_RATING_SORT_INDEX[normalizeRatingKey(label)] = index;
});

function spRatingSortIndex(rating: string): number {
    const key = normalizeRatingKey(rating);
    if (typeof SP_RATING_SORT_INDEX[key] === "number") {
        return SP_RATING_SORT_INDEX[key];
    }
    return SP_RATING_ORDER.length;
}

function sortRatingsBySpScale<T extends { rating: string; value: number }>(rows: T[]): T[] {
    return [...rows].sort((a, b) => {
        const diff = spRatingSortIndex(a.rating) - spRatingSortIndex(b.rating);
        if (diff !== 0) {
            return diff;
        }
        return String(a.rating).localeCompare(String(b.rating));
    });
}

interface CreditRatingProps {
    data: any;
    locale?: Language;
    showHeader: boolean;
    cdsData?: any;
    cdsKey?: string;
    style?: React.CSSProperties | undefined;
    layout?: ChartLayout;
}

export const CreditRating = ({
    data,
    showHeader,
    layout,
    cdsData,
    cdsKey,
    locale = Language.Sv,
    style
}: CreditRatingProps): React.ReactElement => {
    let header = null;
    if (locale === Language.En) {
        header = "Credit Ratings";
    } else if (locale === Language.Sv) {
        header = "Kreditrating";
    }
    const source = cdsData ? data.data.cdsData[cdsKey].ratings : data.data.ratings;
    const ratingsSorted = sortRatingsBySpScale(source);
    const slices = ratingsSorted.map((row) => ({ label: String(row.rating), value: Number(row.value) }));
    const color = chroma("#2186C5");
    const colors = layout?.colorway ?? [0, 0.5, -0.5, 1, -1, 1.5, -1.5, 2, -2, 2.5, -2.5].map((step) => color.darken(step).hex());

    return (
        <React.Fragment>
            {showHeader !== false ? <h3>{header}</h3> : null}
            <DonutChart
                slices={slices}
                colors={colors}
                hole={0.4}
                showLegend={layout?.showlegend ?? false}
                labelMode="label+percent"
                ariaLabel={header || "Credit ratings"}
                height={layoutHeight(layout, 320)}
                width={layoutWidth(layout)}
                style={style}
            />
        </React.Fragment>
    );
};
